import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Trash2,
  AlertTriangle,
  Calendar,
  User
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const AlertView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [alert, setAlert] = useState(null)
  const [loading, setLoading] = useState(true)
  const { getAlerts, deleteAlert, addAlertSubnote } = useFirebase()
  const [subOpen, setSubOpen] = useState(false)
  const [subDesc, setSubDesc] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadAlert()
  }, [id])

  const loadAlert = async () => {
    try {
      setLoading(true)
      const alerts = await getAlerts()
      const foundAlert = alerts.find(a => a.id === id)
      if (foundAlert) {
        setAlert(foundAlert)
      } else {
        toast.error('Note not found')
        navigate('/alerts')
      }
    } catch (error) {
      console.error('Error loading alert:', error)
      toast.error('Failed to load note')
      navigate('/alerts')
    } finally {
      setLoading(false)
    }
  }

  const openAddSubnote = () => {
    setSubOpen(true)
    setSubDesc('')
  }

  const saveSubnote = async () => {
    if (!subDesc.trim()) {
      toast.error('Please enter a description')
      return
    }
    try {
      setSaving(true)
      await addAlertSubnote(id, { title: '', message: subDesc.trim() })
      toast.success('Subnote added')
      setSubOpen(false)
      setSubDesc('')
    } catch (e) {
      console.error('Failed to add subnote:', e)
      toast.error('Failed to add subnote')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await deleteAlert(id)
        toast.success('Alert deleted successfully!')
        navigate('/alerts')
      } catch (error) {
        console.error('Error deleting alert:', error)
        toast.error('Failed to delete alert')
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!alert) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Note not found</h3>
        <button onClick={() => navigate('/alerts')} className="btn-primary">
          Back to Notes
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/alerts')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">View Note</h1>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={openAddSubnote}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add Subnote
          </button>
          <button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Alert Details */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Note Details</h3>
        </div>
        <div className="card-body space-y-6">
          {/* Image */}
          {alert.imageUrl && (
            <div>
              <img src={alert.imageUrl} alt={alert.title} className="w-full max-h-96 object-contain rounded-lg" />
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-900 font-medium">{alert.title}</p>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-900 whitespace-pre-wrap">{alert.message}</p>
            </div>
          </div>

          {/* Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plan
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-900 font-medium">
                {alert.plan || 'Free'}
              </p>
            </div>
          </div>

          {/* Created Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Created Date
            </label>
            <div className="p-3 bg-gray-50 rounded-lg flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <p className="text-gray-900">
                {alert.createdDate
                  ? new Date(alert.createdDate).toLocaleDateString()
                  : (alert.createdAt ? new Date(alert.createdAt).toLocaleDateString() : 'N/A')}
              </p>
            </div>
          </div>
        </div>
      </div>
      {subOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSubOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="text-lg font-semibold text-gray-900 mb-4">Add Subnote</div>
            <textarea
              className="input w-full min-h-[140px]"
              placeholder="Enter description"
              value={subDesc}
              onChange={(e) => setSubDesc(e.target.value)}
              disabled={saving}
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setSubOpen(false)} className="btn-outline" disabled={saving}>Cancel</button>
              <button onClick={saveSubnote} className="btn-primary disabled:opacity-50" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AlertView
